import React from "react";
import { Box, Typography, CircularProgress, linearProgressClasses, useTheme, alpha } from "@mui/material";
import { styled } from "@mui/material/styles";

const LoadingContainer = styled(Box)(({ theme, size }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(size === "small" ? 2 : size === "large" ? 10 : 6),
  minHeight: size === "small" ? 100 : size === "large" ? 500 : 300,
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme, color }) => ({
  width: "80px !important",
  height: "80px !important",
  "& .MuiCircularProgress-circle": {
    strokeLinecap: "round",
  },
  "&.MuiCircularProgress-indeterminate": {
    animationDuration: "1.5s",
  },
}));

const ProgressBar = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 400,
  height: 8,
  borderRadius: 4,
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  margin: theme.spacing(4, 0, 2),
  overflow: "hidden",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "40%",
    background: `linear-gradient(90deg, 
      ${theme.palette.primary.main} 0%, 
      ${theme.palette.primary.light} 50%, 
      ${theme.palette.primary.main} 100%)`,
    borderRadius: 4,
    animation: "loadingShimmer 2s infinite",
  },
  "@keyframes loadingShimmer": {
    "0%": {
      transform: "translateX(-100%)",
    },
    "100%": {
      transform: "translateX(250%)",
    },
  },
}));

const LoadingSpinner = ({ 
  message = "Loading...", 
  size = "medium", 
  variant = "circular",
  color = "primary",
  showProgressBar = false,
  progressValue 
}) => {
  const theme = useTheme();

  const sizes = {
    small: { spinner: 40, fontSize: "0.875rem" },
    medium: { spinner: 60, fontSize: "1rem" },
    large: { spinner: 80, fontSize: "1.125rem" },
  };

  const currentSize = sizes[size];

  if (variant === "progress") {
    return (
      <LoadingContainer size={size}>
        <Typography 
          variant="body1" 
          color="text.secondary" 
          gutterBottom
          sx={{ fontSize: currentSize.fontSize }}
        >
          {message}
        </Typography>
        {showProgressBar && (
          <ProgressBar />
        )}
        {progressValue !== undefined && (
          <Box sx={{ width: "100%", maxWidth: 400, mt: 2 }}>
            <Box
              sx={{
                width: `${Math.min(progressValue, 100)}%`,
                height: 8,
                borderRadius: 4,
                background: `linear-gradient(90deg, 
                  ${theme.palette[color].main} 0%, 
                  ${theme.palette[color].light} 100%)`,
                transition: "width 0.3s ease",
              }}
            />
          </Box>
        )}
      </LoadingContainer>
    );
  }

  return (
    <LoadingContainer size={size}>
      <StyledCircularProgress
        size={currentSize.spinner}
        thickness={4}
        sx={{
          "& .MuiCircularProgress-circle": {
            stroke: `url(#gradient-${color})`,
          },
        }}
      />
      
      <svg width="0" height="0">
        <defs>
          <linearGradient id={`gradient-primary`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={theme.palette.primary.light} />
            <stop offset="50%" stopColor={theme.palette.primary.main} />
            <stop offset="100%" stopColor={theme.palette.primary.dark} />
          </linearGradient>
          <linearGradient id={`gradient-secondary`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={theme.palette.secondary.light} />
            <stop offset="50%" stopColor={theme.palette.secondary.main} />
            <stop offset="100%" stopColor={theme.palette.secondary.dark} />
          </linearGradient>
          <linearGradient id={`gradient-success`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={theme.palette.success.light} />
            <stop offset="50%" stopColor={theme.palette.success.main} />
            <stop offset="100%" stopColor={theme.palette.success.dark} />
          </linearGradient>
        </defs>
      </svg>
      
      <Typography 
        variant="body1" 
        color="text.secondary" 
        sx={{ 
          mt: 3,
          fontSize: currentSize.fontSize,
          fontWeight: 500 
        }}
      >
        {message}
      </Typography>
      
      {showProgressBar && size === "large" && (
        <ProgressBar />
      )}
    </LoadingContainer>
  );
};

export default LoadingSpinner;